import { useState } from 'react';
import OBR from '@owlbear-rodeo/sdk';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import type { HotlistEntry } from '@dcc/rules/rules/hotlist.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { dccCoreConditions } from '@dcc/rules/content/dccCoreConditions.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import {
  clearHotlistSlot,
  hotlistLockedByEncounter,
  moveHotlistSlot,
  setHotlistSlot,
} from '@dcc/rules/app/hotlistService.ts';
import { useHotlistItem } from '@dcc/rules/app/hotlistUseService.ts';
import { hotlistUseOptionsFor } from '@dcc/rules/rules/hotlistUse.ts';
import { useItem } from '@dcc/rules/app/itemUseService.ts';
import { castSupportSpell } from '@dcc/rules/app/spellCastService.ts';
import { isHealingInterruptSpell, isManualCustomSpell } from '@dcc/rules/rules/spellCast.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { deriveStatsFor } from '@dcc/rules/app/derivedStats.ts';
import { VitalsHeader } from './VitalsHeader.tsx';
import { usePlaySheet, type PlaySheetController } from './usePlaySheet.ts';
import {
  hotlistCandidatesFor,
  hotlistCatalogue,
  inventoryCatalogue,
  previewItemUse,
  supportSpellActionCost,
  supportSpellsFor,
} from './inventoryHotlist.ts';
import { markCombatActions } from './combatActionSync.ts';
import { useCheckRoller } from './useCheckRoller.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import { STAT_IDS, STAT_LABELS } from '@dcc/rules/domain/stats.ts';
import { planRollFor } from '@dcc/rules/app/rollService.ts';
import { RollModeContextMenu, type RollModeMenuState } from './RollModeContextMenu.tsx';

export function PlayHotlistPanel(props: {
  play: PlaySheetController;
  services: AppServices;
  characterId: string;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
  showVitals?: boolean;
  onOpenCombat?: (skillId: string, mode?: RollMode) => void;
}) {
  const play = props.play;
  const [configBusy, setConfigBusy] = useState(false);
  const [localNote, setLocalNote] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [targetSlotText, setTargetSlotText] = useState('');
  const [candidateKey, setCandidateKey] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [actionSlot, setActionSlot] = useState<number | null>(null);
  const [actionSkillId, setActionSkillId] = useState<string | null>(null);
  const [actionStat, setActionStat] = useState<StatId | null>(null);
  const [actionMode, setActionMode] = useState<RollMode>('normal');
  const [rollMenu, setRollMenu] = useState<(RollModeMenuState & ({ kind: 'combat'; skillId: string } | { kind: 'check'; skillId: string; stat: StatId })) | null>(null);
  const checkRoller = useCheckRoller(play);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const { character, runtime } = play.records;
  const effects = activeEffects(runtime);
  const effective = buildEffectiveCharacter(character, effects, (id) => contentRegistry.getItem(id));
  const configLock = hotlistLockedByEncounter(runtime);
  const disabled = play.busy || configBusy || checkRoller.rolling !== null;
  const targetSlot = targetSlotText === '' ? null : Number(targetSlotText);
  const candidates = targetSlot === null ? [] : hotlistCandidatesFor(character, targetSlot);
  const selectedCandidate = candidates.find((candidate) => candidate.key === candidateKey) ?? null;
  // Deliberately not a Hook. The loading render returns above; putting a useMemo
  // here changes the Hook count between the loading and loaded renders and React
  // correctly tears the tab down. This calculation is tiny and the service is
  // authoritative anyway.
  const readyOptions = hotlistUseOptionsFor(character, inventoryCatalogue);
  const supportSpells = supportSpellsFor(character);

  function hotlistDeps() {
    return {
      characters: props.services.repository,
      now: () => new Date().toISOString(),
      catalogue: inventoryCatalogue,
      lookupSkill: (id: string) => contentRegistry.getSkill(id),
      runtime,
    };
  }

  async function runConfig(task: () => ReturnType<typeof setHotlistSlot>) {
    if (disabled) return;
    const blocked = props.coordinationGate.claim();
    if (blocked) { setLocalError(blocked.message); return; }
    setConfigBusy(true);
    setLocalError(null);
    try {
      const outcome = await task();
      if (!outcome.ok) {
        setLocalError(outcome.problems.map((problem) => problem.message).join('; '));
        return;
      }
      setLocalNote(outcome.lines.join(' ') || 'Saved.');
      play.reload();
    } catch (error) {
      setLocalError(await props.onSaveError(error));
      play.reload();
    } finally {
      props.coordinationGate.release();
      setConfigBusy(false);
    }
  }

  function preparedUse(itemId: string, slot: number) {
    const preview = previewItemUse(character, runtime, itemId, 'hotlist', slot);
    let sicknessApproved = false;
    if (preview.plan.causesPotionSickness) {
      sicknessApproved = window.confirm(
        'Another potion is still inside the 20-second cadence. Using this one will cause Potion Sickness. Continue?',
      );
      if (!sicknessApproved) return;
    }

    void play.run('The prepared Item could not be used.', async (records) => {
      const live = previewItemUse(records.character, records.runtime, itemId, 'hotlist', slot);
      const outcome = useItem(
        records.character,
        records.runtime,
        itemId,
        'hotlist',
        {
          catalogue: inventoryCatalogue,
          conditions: dccCoreConditions,
          effects: activeEffects(records.runtime),
          newId,
        },
        slot,
      );
      if (!outcome.ok) return outcome.reason;
      if (outcome.potionSickness && !sicknessApproved && !preview.plan.causesPotionSickness) {
        return 'That potion would now cause Potion Sickness. Nothing was used; press Use again to confirm.';
      }
      // The explicit slot identity is rechecked by itemUseService. `live` is
      // still built here so the same current-state preview path is exercised.
      if (!live.plan.canUse) return live.plan.refusal ?? 'That Item cannot be used now.';
      return { character: outcome.character, runtime: outcome.runtime, note: outcome.lines.join(' ') };
    });
  }

  async function cast(skillId: string, execution: 'normal' | 'healing-interrupt') {
    const trackerCost = supportSpellActionCost(character, skillId);
    const saved = await play.run('The Spell could not be cast.', async (records) => {
      const skill = contentRegistry.getSkill(skillId);
      if (!skill) return 'That Spell is not loaded in this player catalogue.';
      const outcome = castSupportSpell(records.character, records.runtime, skill, { catalogue: hotlistCatalogue }, execution);
      if (!outcome.ok) return outcome.reason;
      return { character: outcome.character, runtime: outcome.runtime, note: outcome.lines.join(' ') };
    });
    if (saved) {
      try {
        const tracked = await markCombatActions(props.combatStorage, props.characterId, trackerCost, 'player-spell');
        if (tracked.outOfActions) await OBR.notification.show('Out Of Actions', 'WARNING');
      } catch (error) {
        await OBR.notification.show(error instanceof Error ? error.message : 'Combat tracker update failed.', 'WARNING');
      }
    }
  }


  function runReadyOption(option: (typeof readyOptions)[number]) {
    void play.run('The Hotlist Action could not be saved.', async (records) => {
      const outcome = useHotlistItem(records.character, records.runtime, option.choice, { catalogue: inventoryCatalogue });
      if (!outcome.ok) return outcome.reason;
      return { character: outcome.character, runtime: outcome.runtime, note: outcome.lines.join(' ') };
    });
  }

  function chooseSlotForEdit(slot: number) {
    const entry = character.hotlist[slot] ?? null;
    setTargetSlotText(String(slot));
    setCandidateKey(entry === null ? '' : entry.kind === 'item' ? `item:${entry.itemId}` : `${entry.kind}:${entry.skillId}`);
    setQuantity(entry !== null && entry.kind === 'item' ? entry.quantity : 1);
    setActionSlot(null);
  }

  function clearSlot(slot: number) {
    void runConfig(async () => { const out = await clearHotlistSlot(character, slot, hotlistDeps()); if (out.ok) setTargetSlotText(''); return out; });
  }

  function openActionMenu(slot: number) {
    const entry = character.hotlist[slot] ?? null;
    if (entry === null) { chooseSlotForEdit(slot); return; }
    setActionSlot(actionSlot === slot ? null : slot);
    setActionSkillId(null);
    setActionStat(null);
    setLocalNote(null);
    setLocalError(null);
  }

  function startSkillCheck(skillId: string) {
    setActionSkillId(skillId);
    setActionStat(null);
  }


  return (
    <div className="dcc-play">
      {props.showVitals !== false && <VitalsHeader character={character} derived={deriveStatsFor(effective)} />}

      <section className="dcc-creator-card">
        <h2>Hotlist</h2>
        <p className="dcc-creator-help">Ten quick-access slots. Preparing something changes access and Action cost; it does not grant its equipment bonuses.</p>
        {configLock && <p className="dcc-creator-warn">{configLock}</p>}
        <ol className="dcc-hotlist-list">
          {character.hotlist.map((entry, slot) => {
            const item = entry?.kind === 'item' ? contentRegistry.getItem(entry.itemId) : undefined;
            const skill = entry !== null && entry !== undefined && entry.kind !== 'item'
              ? contentRegistry.getSkill(entry.skillId)
              : undefined;
            const name = entry === null
              ? 'Empty'
              : entry.kind === 'item'
                ? (item?.name ?? entry.itemId)
                : (skill?.name ?? entry.skillId);
            const slotReady = readyOptions.filter((option) => option.choice.slot === slot);
            const usePreview = entry?.kind === 'item' && item?.use
              ? previewItemUse(character, runtime, entry.itemId, 'hotlist', slot)
              : null;
            const attackSkill = skill && contentRegistry.getAttack(skill.id) ? skill : null;
            const linkedWeaponAttack = item?.weaponAttackId
              ? (contentRegistry.getSkill(item.weaponAttackId) ?? null)
              : null;
            const itemAttackSkill = linkedWeaponAttack && contentRegistry.getAttack(linkedWeaponAttack.id)
              ? linkedWeaponAttack
              : null;
            const skillPlan = skill && actionSkillId === skill.id && actionStat !== null
              ? planRollFor(effective, { stat: actionStat, skill })
              : null;

            return (
              <li key={slot} className="dcc-hotlist-row">
                <button
                  type="button"
                  className="dcc-hotlist-quick"
                  disabled={disabled}
                  title={entry === null ? 'Click to set this Hotlist slot. Right-click for slot options.' : 'Click for use/roll options. Right-click to change or clear the slot.'}
                  onClick={() => openActionMenu(slot)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setActionSlot(null);
                    setActionSkillId(null);
                    setActionStat(null);
                    chooseSlotForEdit(slot);
                  }}
                >
                  <span><strong>Slot {slot + 1}: {name}</strong></span>
                  {entry?.kind === 'item' && <span>× {entry.quantity}</span>}
                </button>

                <button type="button" className="dcc-hotlist-edit-button dcc-creator-secondary" disabled={disabled || configLock !== null} onClick={() => chooseSlotForEdit(slot)} aria-label={`Edit Hotlist slot ${slot + 1}`}>Edit</button>
                {actionSlot === slot && entry !== null && (
                  <div className="dcc-hotlist-context" role="menu" aria-label={`Use Hotlist slot ${slot + 1}`}>
                    {entry.kind === 'item' && usePreview?.plan.canUse && (
                      <button disabled={disabled} onClick={() => { setActionSlot(null); preparedUse(entry.itemId, slot); }}>
                        Use {name}
                      </button>
                    )}
                    {entry.kind === 'item' && slotReady.map((option, index) => (
                      <button
                        key={`${slot}-action-${index}-${option.choice.kind}`}
                        className="dcc-creator-secondary"
                        disabled={disabled}
                        onClick={() => { setActionSlot(null); runReadyOption(option); }}
                      >
                        {option.outgoingName ? `Swap ${option.outgoingName} → ${option.incomingName}` : `Ready ${option.incomingName}`}
                      </button>
                    ))}
                    {entry.kind === 'item' && itemAttackSkill && <div className="dcc-sheet-roll-modes" aria-label="Hotlist roll mode">{(['normal', 'advantage', 'disadvantage'] as RollMode[]).map(m => <button type="button" key={m} className={actionMode === m ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'} disabled={disabled} onClick={() => setActionMode(m)}>{m === 'normal' ? 'Normal' : m === 'advantage' ? 'Advantage' : 'Disadvantage'}</button>)}</div>}
                    {entry.kind === 'item' && itemAttackSkill && (
                      <button
                        disabled={disabled || !props.onOpenCombat}
                        onClick={() => { setActionSlot(null); props.onOpenCombat?.(itemAttackSkill.id, actionMode); }}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          setRollMenu({ x: event.clientX, y: event.clientY, label: `Attack: ${itemAttackSkill.name}`, kind: 'combat', skillId: itemAttackSkill.id });
                        }}
                        title="Right-click to choose the Combat roll mode."
                      >
                        Roll {itemAttackSkill.name} in Combat
                      </button>
                    )}
                    {entry.kind === 'spell' && skill && (skill.supportEffect?.target === 'self' || isManualCustomSpell(skill)) && (
                      <>
                        <button disabled={disabled} onClick={() => { setActionSlot(null); void cast(skill.id, 'normal'); }}>
                          Cast {skill.name}
                        </button>
                        {isHealingInterruptSpell(skill) && (
                          <button className="dcc-creator-secondary" disabled={disabled} onClick={() => { setActionSlot(null); void cast(skill.id, 'healing-interrupt'); }}>
                            Healing Interrupt
                          </button>
                        )}
                      </>
                    )}
                    {attackSkill && (
                      <button
                        disabled={disabled || !props.onOpenCombat}
                        onClick={() => { setActionSlot(null); props.onOpenCombat?.(attackSkill.id); }}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          setRollMenu({ x: event.clientX, y: event.clientY, label: `Attack: ${attackSkill.name}`, kind: 'combat', skillId: attackSkill.id });
                        }}
                        title="Right-click to choose the Combat roll mode."
                      >
                        Roll {attackSkill.name} in Combat
                      </button>
                    )}
                    {entry.kind !== 'item' && skill && !attackSkill && skill.supportEffect === undefined && !skill.traits.includes('passive') && (
                      <>
                        <button className="dcc-creator-secondary" disabled={disabled} onClick={() => startSkillCheck(skill.id)}>
                          Roll {skill.name}
                        </button>
                        {actionSkillId === skill.id && (
                          <div className="dcc-hotlist-roll-options">
                            <p className="dcc-creator-help">Choose the Stat for this Check.</p>
                            <div className="dcc-inline-stat-choices">
                              {STAT_IDS.map((stat) => (
                                <button
                                  type="button"
                                  key={stat}
                                  className={actionStat === stat ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'}
                                  disabled={disabled}
                                  onClick={() => setActionStat(stat)}
                                >
                                  {STAT_LABELS[stat].abbr}{skill.commonStat === stat ? ' · common' : ''}
                                </button>
                              ))}
                            </div>
                            <div className="dcc-sheet-roll-modes">
                              {(['normal', 'advantage', 'disadvantage'] as RollMode[]).map((rollMode) => (
                                <button
                                  type="button"
                                  key={rollMode}
                                  className={actionMode === rollMode ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'}
                                  disabled={disabled}
                                  onClick={() => setActionMode(rollMode)}
                                >
                                  {rollMode === 'normal' ? 'Normal' : rollMode === 'advantage' ? 'Advantage' : 'Disadvantage'}
                                </button>
                              ))}
                            </div>
                            {skillPlan && <p className={skillPlan.blockedReason ? 'dcc-creator-warn' : 'dcc-creator-help'}>{skillPlan.blockedReason ?? skillPlan.formula}</p>}
                            <button
                              disabled={disabled || actionStat === null || skillPlan?.ready !== true}
                              onClick={() => {
                                if (actionStat === null) return;
                                void checkRoller.roll({ stat: actionStat, skill }, actionMode).then((saved) => {
                                  if (saved) setActionSlot(null);
                                });
                              }}
                              onContextMenu={(event) => {
                                if (actionStat === null) return;
                                event.preventDefault();
                                setRollMenu({ x: event.clientX, y: event.clientY, label: `${skill.name} Check`, kind: 'check', skillId: skill.id, stat: actionStat });
                              }}
                              title="Right-click to roll straight, with Advantage, or with Disadvantage."
                            >
                              {checkRoller.rolling ?? `Roll ${skill.name}`}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {entry.kind === 'item' && !usePreview?.plan.canUse && slotReady.length === 0 && !itemAttackSkill && (
                      <p className="dcc-creator-help">No direct use is available for this Item right now.</p>
                    )}
                    {entry.kind !== 'item' && !skill && <p className="dcc-creator-warn">This Hotlist entry is not loaded in the current catalogue.</p>}
                    {entry.kind === 'spell' && skill && skill.supportEffect === undefined && !isManualCustomSpell(skill) && !attackSkill && skill.traits.includes('passive') && (
                      <p className="dcc-creator-help">This Passive Spell does not have an automated use yet.</p>
                    )}
                    {entry.kind === 'skill' && skill && skill.traits.includes('passive') && !attackSkill && (
                      <p className="dcc-creator-help">This is a Passive Skill and does not make a Skill Check.</p>
                    )}
                    <button className="dcc-creator-secondary" onClick={() => { setActionSlot(null); setActionSkillId(null); setActionStat(null); }}>Close</button>
                  </div>
                )}

{targetSlot === slot && (
      <section className="dcc-hotlist-editor" aria-label={`Edit Hotlist slot ${slot + 1}`}>
        <h3>Edit slot {slot + 1}</h3>
        <p className="dcc-creator-help">Changing a slot never deletes the underlying Item or Spell.</p>
        <label className="dcc-creator-field">
          Item or Spell
          <select
            value={candidateKey}
            disabled={disabled || configLock !== null || targetSlot === null}
            onChange={(event) => {
              const key = event.target.value;
              setCandidateKey(key);
              const candidate = candidates.find((entry) => entry.key === key);
              setQuantity(candidate?.defaultQuantity ?? 1);
            }}
          >
            <option value="">Choose something…</option>
            {candidates.map((candidate) => <option key={candidate.key} value={candidate.key}>{candidate.name}</option>)}
          </select>
        </label>
        {selectedCandidate?.entry.kind === 'item' && (
          <label className="dcc-creator-field">
            Copies prepared
            <input
              type="number"
              min={1}
              max={999}
              value={quantity}
              disabled={disabled || configLock !== null}
              onChange={(event) => setQuantity(Number(event.target.value))}
            />
          </label>
        )}
        {targetSlot !== null && candidates.length === 0 && <p className="dcc-empty">Nothing this crawler owns or knows can be placed in that slot.</p>}
        <div className="dcc-creator-nav">
          <button
            disabled={disabled || configLock !== null || targetSlot === null || selectedCandidate === null}
            onClick={() => {
              if (targetSlot === null || selectedCandidate === null) return;
              const entry: HotlistEntry = selectedCandidate.entry.kind === 'item'
                ? { ...selectedCandidate.entry, quantity }
                : selectedCandidate.entry;
              void runConfig(async () => {
                const outcome = await setHotlistSlot(character, targetSlot, entry, hotlistDeps());
                if (outcome.ok) {
                  setTargetSlotText('');
                  setCandidateKey('');
                  setQuantity(1);
                }
                return outcome;
              });
            }}
          >Save Hotlist slot</button>
          {entry !== null && <button className="dcc-danger" disabled={disabled || configLock !== null} onClick={() => clearSlot(slot)}>Clear slot</button>}
          <button className="dcc-creator-secondary" disabled={disabled || configLock !== null || slot === 0} onClick={() => void runConfig(() => moveHotlistSlot(character, slot, slot - 1, hotlistDeps()))}>Move up</button>
          <button className="dcc-creator-secondary" disabled={disabled || configLock !== null || slot === character.hotlist.length - 1} onClick={() => void runConfig(() => moveHotlistSlot(character, slot, slot + 1, hotlistDeps()))}>Move down</button>
          <button className="dcc-creator-secondary" disabled={disabled} onClick={() => { setTargetSlotText(''); setCandidateKey(''); setQuantity(1); }}>Cancel</button>
        </div>
      </section>

      )}


              </li>
            );
          })}
        </ol>
      </section>

      <section className="dcc-creator-card">
        <h2>Support Spells</h2>
        {supportSpells.length === 0 ? (
          <p className="dcc-empty">No known support Spells.</p>
        ) : supportSpells.map((skill) => {
          const actionCost = supportSpellActionCost(character, skill.id);
          const automated = skill.supportEffect?.target === 'self' || isManualCustomSpell(skill);
          return (
            <div key={skill.id} className="dcc-support-spell">
              <div className="dcc-item-title">
                <strong>{skill.name}</strong>
                <span>{skill.manaCost ?? 0} Mana</span>
              </div>
              <p className="dcc-creator-help">
                {character.hotlist.some((entry) => entry?.kind === 'spell' && entry.skillId === skill.id)
                  ? `${actionCost} Action when in combat · prepared`
                  : `${actionCost} Actions when in combat · not prepared`}
              </p>
              {!automated && <p className="dcc-creator-help">{skill.supportEffect?.target === 'pet' ? 'Select a companion in the Companion tab to cast this Spell.' : 'This Spell is known, but its effect is not automated yet.'}</p>}
              {isManualCustomSpell(skill) && <p className="dcc-creator-help">{skill.description} Casting spends Mana/Actions only; the GM resolves the effect.</p>}
              <div className="dcc-item-actions">
                <button disabled={disabled || !automated} onClick={() => void cast(skill.id, 'normal')}>Cast</button>
                {isHealingInterruptSpell(skill) && (
                  <button className="dcc-creator-secondary" disabled={disabled || !automated} onClick={() => void cast(skill.id, 'healing-interrupt')}>
                    Healing Interrupt
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {(localError || play.error) && <p className="dcc-save-status dcc-save-warn" role="alert">{localError ?? play.error}</p>}
      {(localNote || play.note) && <p className="dcc-save-status" role="status">{localNote ?? play.note}</p>}
      <RollModeContextMenu
        menu={rollMenu}
        onClose={() => setRollMenu(null)}
        onChoose={(mode) => {
          const target = rollMenu;
          setRollMenu(null);
          if (!target) return;
          const selected = contentRegistry.getSkill(target.skillId);
          if (!selected) return;
          if (target.kind === 'combat') {
            setActionSlot(null);
            props.onOpenCombat?.(target.skillId, mode);
          } else {
            void checkRoller.roll({ stat: target.stat, skill: selected }, mode).then((saved) => { if (saved) setActionSlot(null); });
          }
        }}
      />
    </div>
  );
}


export function PlayHotlistTab(props: {
  services: AppServices;
  characterId: string;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate);
  return <PlayHotlistPanel {...props} play={play} />;
}
